import { text } from 'stream/consumers';
import { RiskStatusTypes } from '../../risk/enums/riskStatusTypes';
import { Colors } from '../enums/colors';
import { RiskRAGStatuses } from '../enums/planning/riskRagStatuses';
import { RiskProfilerAnswerScoreTypes } from '../enums/risks/riskProfilerAnswerScoreTypes';
import { RAGBreakdown } from '../models/rag/ragBreakdown';

export class RiskRagHelper {
  public static getRagStatusObjects(
    accountID: number,
    useOnlyColor: boolean
  ): { value: RiskRAGStatuses; color: string; description: string }[] {
    const ragStatuses: { value: RiskRAGStatuses; color: string; description: string }[] = [];
    ragStatuses.push({
      value: RiskRAGStatuses.Low,
      description: this.getRAGDescriptionTextFromValue(RiskRAGStatuses.Low, accountID, useOnlyColor),
      color: this.getRagBoxFromValue(RiskRAGStatuses.Low),
    });
    ragStatuses.push({
      value: RiskRAGStatuses.Minor,
      description: this.getRAGDescriptionTextFromValue(RiskRAGStatuses.Minor, accountID, useOnlyColor),
      color: this.getRagBoxFromValue(RiskRAGStatuses.Minor),
    });
    ragStatuses.push({
      value: RiskRAGStatuses.Medium,
      description: this.getRAGDescriptionTextFromValue(RiskRAGStatuses.Medium, accountID, useOnlyColor),
      color: this.getRagBoxFromValue(RiskRAGStatuses.Medium),
    });
    ragStatuses.push({
      value: RiskRAGStatuses.High,
      description: this.getRAGDescriptionTextFromValue(RiskRAGStatuses.High, accountID, useOnlyColor),
      color: this.getRagBoxFromValue(RiskRAGStatuses.High),
    });
    ragStatuses.push({
      value: RiskRAGStatuses.Extreme,
      description: this.getRAGDescriptionTextFromValue(RiskRAGStatuses.Extreme, accountID, useOnlyColor),
      color: this.getRagBoxFromValue(RiskRAGStatuses.Extreme),
    });
    return ragStatuses;
  }

  public static getRAGColourTextFromValue(ragStatus: RiskRAGStatuses) {
    switch (ragStatus) {
      case RiskRAGStatuses.Low:
        return 'Low';
      case RiskRAGStatuses.Minor:
        return 'Minor';
      case RiskRAGStatuses.Medium:
        return 'Medium';
      case RiskRAGStatuses.High:
        return 'High';
      case RiskRAGStatuses.Extreme:
        return 'Extreme';
    }
  }

  public static getRAGColourHexFromValue(ragStatus: RiskRAGStatuses) {
    switch (ragStatus) {
      case RiskRAGStatuses.Low:
        return Colors.Green;
      case RiskRAGStatuses.Minor:
        return Colors.Yellow;
      case RiskRAGStatuses.Medium:
        return Colors.Amber;
      case RiskRAGStatuses.High:
        return Colors.Orange;
      case RiskRAGStatuses.Extreme:
        return Colors.Red;
    }
  }

  public static getRAGBackgroundColourHexFromValue(ragStatus: RiskRAGStatuses) {
    switch (ragStatus) {
      case RiskRAGStatuses.Low:
        return Colors.BG_Green;
      case RiskRAGStatuses.Minor:
        return Colors.BG_Yellow;
      case RiskRAGStatuses.Medium:
        return Colors.BG_Amber;
      case RiskRAGStatuses.High:
        return Colors.BG_Orange;
      case RiskRAGStatuses.Extreme:
        return Colors.BG_Red;
    }
  }

  public static getRiskProfilerAnswerColorFromValue(score: RiskProfilerAnswerScoreTypes) {
    switch (score) {
      case RiskProfilerAnswerScoreTypes.Low:
        return Colors.Green;
      case RiskProfilerAnswerScoreTypes.Moderate:
        return Colors.Yellow;
      case RiskProfilerAnswerScoreTypes.Medium:
        return Colors.Amber;
      case RiskProfilerAnswerScoreTypes.High:
        return Colors.Orange;
      case RiskProfilerAnswerScoreTypes.Significant:
        return Colors.Red;
      default:
        return Colors.Momentus_Grey;
    }
  }

  public static getRiskProfileScoreCardColorAndDescription(score: RiskProfilerAnswerScoreTypes) {
    switch (score) {
      case RiskProfilerAnswerScoreTypes.Low:
        return { colour: Colors.Green, bgColor: Colors.BG_Green, text: 'Low', icon: 'warning'}
      case RiskProfilerAnswerScoreTypes.Moderate:
        return { colour: Colors.Yellow, bgColor: Colors.BG_Yellow, text: 'Moderate', icon: 'warning'}
      case RiskProfilerAnswerScoreTypes.Medium:
        return { colour: Colors.Amber, bgColor: Colors.BG_Amber, text: 'Medium', icon: 'warning'}
      case RiskProfilerAnswerScoreTypes.High:
        return { colour: Colors.Orange, bgColor: Colors.BG_Orange, text: 'High', icon: 'warning'}
      case RiskProfilerAnswerScoreTypes.Significant:
        return { colour: Colors.Red, bgColor: Colors.BG_Red, text: 'Significant', icon: 'warning'}
      default:
        return { colour: Colors.Grey, bgColor: Colors.Momentus_White, text: 'No responses', icon: ''}
    }
  }

  public static getRagBoxFromValue(ragStatus: RiskRAGStatuses) {
    switch (ragStatus) {
      case RiskRAGStatuses.Low:
        return 'rag-box-0';
      case RiskRAGStatuses.Minor:
        return 'rag-box-1';
      case RiskRAGStatuses.Medium:
        return 'rag-box-2';
      case RiskRAGStatuses.High:
        return 'rag-box-3';
      case RiskRAGStatuses.Extreme:
        return 'rag-box-4';
    }
  }

  public static getRAGDescriptionTextFromValue(ragStatus: RiskRAGStatuses, accountID: number, useOnlyColor: boolean) {
    const color = this.getRAGColourTextFromValue(ragStatus);

    if (useOnlyColor) {
      return color;
    }

    let description: string = '';
    switch (ragStatus) {
      case RiskRAGStatuses.Low:
        description = 'Low';
        break;
      case RiskRAGStatuses.Minor:
        description = 'Minor';
        break;
      case RiskRAGStatuses.Medium:
        description = 'Medium';
        break;
      case RiskRAGStatuses.High:
        description = 'High';
        break;
      case RiskRAGStatuses.Extreme:
        description = 'Extreme';
        break;
    }

    return color + ' (' + description + ')';
  }

  public static getRAGDescriptionFromValue(ragStatus: RiskRAGStatuses) {
    let description: string = '';
    switch (ragStatus) {
      case RiskRAGStatuses.Low:
        description = 'Low';
        break;
      case RiskRAGStatuses.Minor:
        description = 'Minor';
        break;
      case RiskRAGStatuses.Medium:
        description = 'Medium';
        break;
      case RiskRAGStatuses.High:
        description = 'High';
        break;
      case RiskRAGStatuses.Extreme:
        description = 'Extreme';
        break;
    }
    return description;
  }

  public static getTotalRAGBreakdownBar(rags: RiskRAGStatuses[]): RAGBreakdown[] {
    const list = [
      {
        ragStatus: RiskRAGStatuses.Low,
        colour: RiskRagHelper.getRAGColourHexFromValue(RiskRAGStatuses.Low),
        count: rags.filter((r) => r === RiskRAGStatuses.Low).length,
        text: this.getRAGDescriptionFromValue(RiskRAGStatuses.Low),
      },
      {
        ragStatus: RiskRAGStatuses.Minor,
        colour: RiskRagHelper.getRAGColourHexFromValue(RiskRAGStatuses.Minor),
        count: rags.filter((r) => r === RiskRAGStatuses.Minor).length,
        text: this.getRAGDescriptionFromValue(RiskRAGStatuses.Minor),
      },
      {
        ragStatus: RiskRAGStatuses.Medium,
        colour: RiskRagHelper.getRAGColourHexFromValue(RiskRAGStatuses.Medium),
        count: rags.filter((r) => r === RiskRAGStatuses.Medium).length,
        text: this.getRAGDescriptionFromValue(RiskRAGStatuses.Medium),
      },
      {
        ragStatus: RiskRAGStatuses.High,
        colour: RiskRagHelper.getRAGColourHexFromValue(RiskRAGStatuses.High),
        count: rags.filter((r) => r === RiskRAGStatuses.High).length,
        text: this.getRAGDescriptionFromValue(RiskRAGStatuses.High),
      },
      {
        ragStatus: RiskRAGStatuses.Extreme,
        colour: RiskRagHelper.getRAGColourHexFromValue(RiskRAGStatuses.Extreme),
        count: rags.filter((r) => r === RiskRAGStatuses.Extreme).length,
        text: this.getRAGDescriptionFromValue(RiskRAGStatuses.Extreme),
      },
    ];

    return list;
  }

  static getRiskStatusColorHex(status: RiskStatusTypes): string {
    switch (status) {
      case RiskStatusTypes.Open: {
        return Colors.Black;
      }
      case RiskStatusTypes.Draft: {
        return Colors.Grey;
      }
      case RiskStatusTypes.Closed: {
        return Colors.Blue_Dark;
      }
      default: {
        return '#000000';
      }
    }
  }
}
